import { Progress, Spin, message } from "antd";
import axios from "axios";
import { UseLazyApiCallV2 } from "hooks/use-lazy-api-call-v2";
import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { ConditionalRenderer } from "components";
import { CancelIcon } from "pages/m2c-simulation/icons";
import { useCurrentUserContext } from "providers";

export const FileUpload = ({ allSettingsSelected, getUploadedVideosData }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [binaryFile, setBinaryFile] = useState("");
  const [signedUrl, setSignedUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(null);
  const [cancelToken, setCancelToken] = useState(null);
  const [uniqueFileName, setUniqueFileName] = useState("");
  const { user } = useCurrentUserContext();

  const [
    uploadVideo,
    {
      data: uploadVideoData,
      isLoading: uploadingVideo,
      error: uploadingVideoError,
    },
  ] = UseLazyApiCallV2({
    url: "/add_video_simulation",
    method: "POST",
  });

  useEffect(() => {
    if (!!binaryFile && signedUrl) {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const source = axios.CancelToken.source();
      setCancelToken(source);
      uploadOnBucket();
    }
  }, [binaryFile, signedUrl]);

  useEffect(() => {
    return () => {
      if (cancelToken) {
        cancelToken.cancel("Component unmounted: Upload canceled");
      }
    };
  }, [cancelToken]);

  function startUploading({ file }) {
    setSelectedFile(file);
    handleUpload(file);
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    startUploading({ file });
  };

  const calculateRemainingTime = (startTime, uploaded, totalSize) => {
    const currentTime = Date.now();
    const elapsedTime = (currentTime - startTime) / 1000; // in seconds
    const uploadedPerSecond = uploaded / elapsedTime;
    const remainingSize = totalSize - uploaded;
    const remainingTimeSeconds = remainingSize / uploadedPerSecond;
    return remainingTimeSeconds;
  };

  async function sendSettingsData() {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...allSettingsSelected,
          filename: uniqueFileName,
          email: user?.email,
        }),
      };
      const res = await fetch(
        "https://p4gqcrbiyb.execute-api.eu-west-3.amazonaws.com/dev/add_video_information",
        requestOptions
      );
    } catch (error) {
      message.error(error.message);
    }
  }

  async function uploadOnBucket() {
    const startTime = Date.now();
    const source = axios.CancelToken.source();
    setCancelToken(source);

    try {
      const response = await axios.put(signedUrl, binaryFile, {
        headers: {
          withCredentials: true,
          "Content-type": selectedFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
          const remainingTimeSeconds = calculateRemainingTime(
            startTime,
            progressEvent.loaded,
            progressEvent.total
          );
          setRemainingTime(remainingTimeSeconds);
        },
        cancelToken: source.token,
      });
      if (response?.status === 200) {
        message.success(`File Uploaded Successfully.`);
        setAllStatesToDefault();
        sendSettingsData();
        //   setLoading(false);
      }
    } catch (error) {
      console.log(error);
      message.error(`${error?.message}`);
    } finally {
      setTimeout(() => {
        getUploadedVideosData();
      }, 3000);
    }
  }

  const handleUpload = async (file) => {
    if (cancelToken) {
      cancelToken.cancel("Upload canceled");
    }

    const videoType = "mp4";
    const videoPath = "m2c-web-000";
    const currentTimeSatmp = new Date().getTime();
    const videoTitle = `${videoPath}-${currentTimeSatmp}`;
    const filename = `${videoTitle}.${videoType}`;

    setUniqueFileName(filename);

    const reader = new FileReader();
    if (file) {
      reader.readAsArrayBuffer(file);

      reader.addEventListener("load", () => setBinaryFile(reader.result));

      if (allSettingsSelected?.modelType?.length < 1)
        return message.error("Please select at least one model type");

      const response = await uploadVideo({
        params: {
          filename: `${user?.email}/${filename}`,
          fileMimeType: file.type,
        },
      });

      if (response) {
        setSignedUrl(response.body.presigned_url);
      }
    }
  };

  function handleClickBrowse() {
    if (!!selectedFile) return;
    setAllStatesToDefault();
    document.getElementById("file-input").click();
  }
  function setAllStatesToDefault() {
    setSelectedFile("");
    setBinaryFile("");
    setProgress(0);
    setSignedUrl("");
  }

  const cancelUpload = () => {
    if (cancelToken) {
      cancelToken.cancel("Upload canceled");

      setAllStatesToDefault();
      message.error("uploading cancelled");
    }
  };

  function handleDropFile(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    startUploading({ file });
  }
  function handleDragOver(e) {
    e.preventDefault();
  }

  return (
    <Spin spinning={uploadingVideo}>
      <div
        className={styles.uploadFileContainer}
        onDrop={handleDropFile}
        onDragOver={handleDragOver}
        id="file-upload"
      >
        <div className={styles.selectFileBox}>
          <div>Drag your file(s) to start uploading</div>
          <div>OR</div>
          <button className={styles.browseBtn} onClick={handleClickBrowse}>
            Browse files
          </button>
        </div>
        <input
          style={{ display: "none" }}
          id="file-input"
          type="file"
          onChange={handleFileChange}
          accept="video/mp4,video/mkv, video/x-m4v,video/*"
        />
        <div>
          <ConditionalRenderer
            condition={!!selectedFile && progress > 0 && progress < 100}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className={styles.fileProgress}>
                <span>
                  Selected File: {selectedFile?.name} {progress}%{" "}
                </span>
                . {Math.round(remainingTime)} sec remaining
              </div>
              <CancelIcon
                onClick={cancelUpload}
                className={styles.cancelIcon}
              />
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={progress > 0}>
            <Progress strokeLinecap="butt" percent={progress} />
          </ConditionalRenderer>
        </div>
      </div>
    </Spin>
  );
};
